import { ReactNode } from 'react';

import DOCX_ICON from '@backrs_ui/common-ui/src/assets/images/document-icons/docx_file_icon.svg';
import IMG_ICON from '@backrs_ui/common-ui/src/assets/images/document-icons/img_file_icon.svg';
import LINK_ICON from '@backrs_ui/common-ui/src/assets/images/document-icons/link_file_icon.svg';
import MOV_ICON from '@backrs_ui/common-ui/src/assets/images/document-icons/mov_file_icon.svg';
import MP4_ICON from '@backrs_ui/common-ui/src/assets/images/document-icons/mp4_file_icon.svg';
import PDF_ICON from '@backrs_ui/common-ui/src/assets/images/document-icons/pdf_file_icon.svg';
import TXT_ICON from '@backrs_ui/common-ui/src/assets/images/document-icons/txt_file_icon.svg';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Typography, SxProps, Theme } from '@mui/material';

const DocumentFileTypes = {
  PDF: '.pdf',
  DOCX: '.docx',
  TXT: '.txt',
  LINK: '.link',

  MP4: '.mp4',
  MOV: '.mov',

  PNG: '.png',
  JPG: '.jpg',
  JPEG: '.jpeg',
  GIF: '.gif',
} as const;

const DocumentIconFileMap = {
  [DocumentFileTypes.PDF]: PDF_ICON,
  [DocumentFileTypes.DOCX]: DOCX_ICON,
  [DocumentFileTypes.TXT]: TXT_ICON,
  [DocumentFileTypes.LINK]: LINK_ICON,

  [DocumentFileTypes.MP4]: MP4_ICON,
  [DocumentFileTypes.MOV]: MOV_ICON,

  [DocumentFileTypes.PNG]: IMG_ICON,
  [DocumentFileTypes.JPG]: IMG_ICON,
  [DocumentFileTypes.JPEG]: IMG_ICON,
  [DocumentFileTypes.GIF]: IMG_ICON,
} as const;

export type UploadedFileProps = Readonly<{
  fileName: string;
  fileSize: string;
  fileType?: string;
  handleDelete?: (fileName: string) => void;
  borderColor?: 'light' | 'dark';
  endAdornment?: ReactNode;
}>;

const getFileIcon = (fileType?: string): string => {
  if (!fileType) return PDF_ICON;

  const lower = fileType.toLowerCase();
  const normalizedType = lower.startsWith('.') ? lower : `.${lower}`;

  return DocumentIconFileMap[normalizedType as keyof typeof DocumentIconFileMap] || PDF_ICON;
};

export const UploadedFile = ({
  fileName,
  fileSize,
  fileType,
  handleDelete,
  borderColor = 'light',
  endAdornment,
}: UploadedFileProps) => {
  const fileIcon = getFileIcon(fileType);

  return (
    <Box sx={uploadedFileContainerSx(borderColor)}>
      <Box sx={fileInfoContainerSx}>
        <Box sx={fileIconSx} component="img" src={fileIcon} alt={`${fileType ?? 'file'} icon`} />
        <Box overflow="hidden" maxWidth="100%">
          <Typography
            variant="text-sm-medium"
            noWrap
            overflow="hidden"
            color={(theme: Theme) => theme.backrs.colors['Gray/700']}
          >
            {fileName.length > 35 ? `${fileName.substring(0, 35)}...` : fileName}
          </Typography>
          {fileSize && (
            <Typography
              variant="text-sm-regular"
              noWrap
              overflow="hidden"
              color={(theme: Theme) => theme.backrs.colors['Gray/600']}
            >
              {fileSize}
            </Typography>
          )}
        </Box>
      </Box>

      {(endAdornment || handleDelete) && (
        <Box sx={endSlotSx}>
          {endAdornment}
          {handleDelete && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(fileName);
              }}
              sx={deleteContainerSx}
            >
              <AppIcon iconName="Trash01" diameter="16px" color={'Gray/400'} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const fileInfoContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  overflow: 'hidden',
});

const endSlotSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
};

const deleteContainerSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-md'],
  paddingTop: backrs.spacing['spacing-none'],
  cursor: 'pointer',
});

const fileIconSx: SxProps<Theme> = {
  width: '40px',
  height: '40px',
};

const uploadedFileContainerSx =
  (borderColor: 'light' | 'dark'): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    padding: backrs.spacing['spacing-xl'],
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: backrs.spacing['spacing-xs'],
    borderRadius: backrs.radius['radius-xl'],
    border: `1px solid ${backrs.colors[borderColor === 'light' ? 'Gray/200' : 'Colors/Foreground/fg-quaternary-hover']}`,
    background: backrs.colors['Colors/Base/White'],
    width: '100%',

    maxWidth: '70vw',
  });
